import { useRef, useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Badge,
  IconButton,
  useDisclosure,
  SimpleGrid,
  Wrap,
  WrapItem,
  Dialog,
  Portal,
  Separator,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiEdit2, FiTrash2, FiArrowLeft, FiBriefcase, FiPlus } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { useBusinessDetail, useBusinessEmployees, useDeleteBusiness } from '../hooks/useBusiness'
import { useMapBoundsAndZoom } from '../hooks/useMapBoundsAndZoom'
import { BusinessFormModal } from '../components/business/BusinessFormModal'
import { AffiliateUnionModal } from '../components/business/AffiliateUnionModal'
import { LinkExistingWorksiteModal } from '../components/business/LinkExistingWorksiteModal'
import { ArrivalInstructionCard } from '../components/business/ArrivalInstructionCard'
import { BusinessWorksiteMap } from '../components/business/BusinessWorksiteMap'
import { BusinessEmployeeTable } from '../components/business/BusinessEmployeeTable'
import { InlineEditTextarea } from '../components/shared/InlineEditTextarea'
import { InfoField } from '../components/shared/InfoField'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared'
import { businessApi } from '../lib/api'
import type { BusinessUnionAffiliation, BusinessWorksiteItem } from '../types/business'

// Inline edit modal for business metadata
import { BusinessChainFormModal } from '../components/business/BusinessChainManager'

const UNION_STATUS_COLORS: Record<string, string> = {
  Covered: 'green',
  Excluded: 'red',
  Local: 'purple',
}

export function BusinessDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const cancelRef = useRef<HTMLButtonElement>(null!)
  const editModal = useDisclosure()
  const affiliateModal = useDisclosure()
  const addWorksiteModal = useDisclosure()
  const linkWorksiteModal = useDisclosure()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [empPage, setEmpPage] = useState(1)
  const [empSearchInput, setEmpSearchInput] = useState('')
  const [empSearch, setEmpSearch] = useState<string | undefined>(undefined)

  const { data: business, isLoading, error, refetch } = useBusinessDetail(id)
  const { data: empData, isLoading: empLoading } = useBusinessEmployees(id, { page: empPage, pageSize: 25, search: empSearch })

  const submitEmpSearch = () => {
    setEmpSearch(empSearchInput.trim() || undefined)
    setEmpPage(1)
  }
  const deleteMutation = useDeleteBusiness()

  const hasUnions = (business?.unionAffiliations.length ?? 0) > 0
  const multiUnion = (business?.unionAffiliations.length ?? 0) > 1

  const { initialViewState } = useMapBoundsAndZoom(business?.worksites ?? [])

  const handleSaveNotes = async (_field: string, value: string) => {
    if (!id) return
    try {
      await businessApi.updateNotes(id, { notes: value })
      refetch()
      toaster.create({ title: 'Notes updated', type: 'success', duration: 2000 })
    } catch {
      toaster.create({ title: 'Failed to update notes', type: 'error', duration: 3000 })
    }
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteMutation.mutateAsync(id)
      toaster.create({ title: 'Business deleted', type: 'success', duration: 3000 })
      navigate('/dashboard/worksites')
    } catch {
      toaster.create({ title: 'Failed to delete business', type: 'error', duration: 3000 })
    }
  }

  if (isLoading) {
    return (
      <VStack py={12}>
        <Spinner size="lg" color="accent" />
        <Text color="gray.500">Loading business...</Text>
      </VStack>
    )
  }

  if (!!error || !business) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <Box bg="red.50" p={4} borderRadius="md" color="red.600">
          Business not found or an error occurred.
        </Box>
        <Button mt={4} variant="ghost" onClick={() => navigate('/dashboard/worksites')}><FiArrowLeft />Back to Worksites
                  </Button>
      </Box>
    );
  }

  const unionColumns: ResponsiveColumn<BusinessUnionAffiliation>[] = [
    {
      header: 'Union',
      mobileRole: 'title',
      cell: (ua) => <Text fontWeight="medium" fontSize="sm">{ua.unionName}</Text>,
    },
    {
      header: 'Local #',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.localNumber || '---'}</Text>,
    },
    {
      header: 'Union Rep',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.unionRepName || '---'}</Text>,
    },
    {
      header: 'Employer Contact',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.employerContactName || '---'}</Text>,
    },
    {
      header: 'Contract Start',
      mobileRole: 'desktop-only',
      cell: (ua) => <Text fontSize="sm">{ua.contractStartDate ? new Date(ua.contractStartDate).toLocaleDateString() : '---'}</Text>,
    },
    {
      header: 'Contract End',
      mobileRole: 'desktop-only',
      cell: (ua) => <Text fontSize="sm">{ua.contractEndDate ? new Date(ua.contractEndDate).toLocaleDateString() : '---'}</Text>,
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (ua) => (
        <Badge colorPalette={ua.isActive ? 'green' : 'gray'} fontSize="xs">
          {ua.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ]

  const worksiteColumns: ResponsiveColumn<BusinessWorksiteItem>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (ws) => <Text fontWeight="medium" fontSize="sm">{ws.label}</Text>,
    },
    ...(hasUnions ? [{
      header: 'Union Affiliation',
      mobileRole: 'meta' as const,
      cell: (ws: BusinessWorksiteItem) => (
        ws.unionStatuses.length === 0 ? (
          <Text fontSize="xs" color="gray.400">---</Text>
        ) : (
          <Wrap gap={1}>
            {ws.unionStatuses.map(us => (
              <WrapItem key={us.unionId}>
                <Badge colorPalette={UNION_STATUS_COLORS[us.status] || 'gray'} fontSize="xs">
                  {multiUnion ? `${us.unionName}: ${us.status}` : us.status}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        )
      ),
    }] : []),
    {
      header: 'Type',
      mobileRole: 'badge',
      cell: (ws) => ws.type ? <Badge bg="info.subtle" color="info.fg" fontSize="xs">{ws.type}</Badge> : null,
    },
    {
      header: 'Location',
      mobileRole: 'meta',
      cell: (ws) => (
        <Text fontSize="sm" color="gray.600">
          {[ws.city, ws.state].filter(Boolean).join(', ') || '---'}
        </Text>
      ),
    },
    {
      header: 'Employees',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (ws) => <Text fontSize="sm">{ws.employeeCount}</Text>,
    },
    {
      header: 'Phone',
      mobileRole: 'desktop-only',
      cell: (ws) => <Text fontSize="sm" color="gray.600">{ws.phone || '---'}</Text>,
    },
  ]

  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch">
        {/* Back + actions header */}
        <HStack justify="space-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/worksites')}><FiArrowLeft />Back to Worksites
                      </Button>
          <HStack gap={2}>
            <Button size="sm" colorPalette="brand" onClick={editModal.onOpen}><FiEdit2 />Edit
                          </Button>
            <IconButton
              aria-label="Delete"
              size="sm"
              variant="ghost"
              colorPalette="red"
              onClick={() => setShowDeleteDialog(true)}><FiTrash2 /></IconButton>
          </HStack>
        </HStack>

        {/* Business metadata card */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack gap={4} mb={4}>
            <Box
              w={12} h={12} bg="accent.soft" borderRadius="lg"
              display="flex" alignItems="center" justifyContent="center"
            >
              <Box boxSize={6} color="accent" asChild><FiBriefcase /></Box>
            </Box>
            <Box flex={1}>
              <HStack gap={2}>
                <Text fontSize="xl" fontWeight="bold">{business.label}</Text>
                {business.type && <Badge bg="info.subtle" color="info.fg">{business.type}</Badge>}
                {!business.isActive && <Badge colorPalette="red">Inactive</Badge>}
              </HStack>
              {business.description && (
                <Text fontSize="sm" color="gray.600" mt={1}>{business.description}</Text>
              )}
            </Box>
          </HStack>

          <Separator mb={4} />

          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
            <InfoField label="Locations" value={`${business.worksites.length} worksite${business.worksites.length !== 1 ? 's' : ''}`} />
            <InfoField label="Total Employees" value={String(business.totalEmployeeCount)} />
            <InfoField label="Created" value={new Date(business.createdAt).toLocaleDateString()} />
          </SimpleGrid>

          <Separator my={4} />

          <InlineEditTextarea
            label="Notes"
            value={business.notes || ''}
            fieldName="notes"
            canEdit={true}
            onSave={handleSaveNotes}
          />
        </Box>

        {/* Union Affiliations */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack justify="space-between" mb={4}>
            <Text fontSize="lg" fontWeight="semibold">
              Union Affiliations ({business.unionAffiliations.length})
            </Text>
            <Button
              size="xs"
              colorPalette="brand"
              variant="outline"
              onClick={affiliateModal.onOpen}><FiPlus />Add
                          </Button>
          </HStack>
          {business.unionAffiliations.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No union affiliations.</Text>
          ) : (
            <ResponsiveTable
              bare
              columns={unionColumns}
              rows={business.unionAffiliations}
              keyFn={(ua) => ua.businessUnionId}
            />
          )}
        </Box>

        {/* O3: arrival instructions (union-tier module, same gate as Union Affiliations) */}
        <ArrivalInstructionCard kind="business" id={business.id} chainLabel={business.label} />

        {/* Locations Map */}
        <BusinessWorksiteMap worksites={business.worksites} initialViewState={initialViewState} fullBleedOnMobile />

        {/* Worksites grid */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2} align="center">
            <Text fontSize="lg" fontWeight="semibold">
              Worksites ({business.worksites.length})
            </Text>
            <HStack gap={2} flexWrap="wrap">
              <Button
                size="xs"
                colorPalette="brand"
                variant="outline"
                onClick={linkWorksiteModal.onOpen}><FiPlus />Add Existing Worksite
                              </Button>
              <Button
                size="xs"
                colorPalette="brand"
                variant="outline"
                onClick={addWorksiteModal.onOpen}><FiPlus />Add New Worksite
                              </Button>
            </HStack>
          </HStack>
          {business.worksites.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No worksites for this business.</Text>
          ) : (
            <ResponsiveTable
              bare
              columns={worksiteColumns}
              rows={business.worksites}
              keyFn={(ws) => ws.id}
              onRowClick={(ws) => navigate(`/dashboard/worksites/${ws.id}`)}
            />
          )}
        </Box>

        {/* Employees grid */}
        <BusinessEmployeeTable
          totalEmployeeCount={business.totalEmployeeCount}
          empData={empData}
          empLoading={empLoading}
          setEmpPage={setEmpPage}
          searchInput={empSearchInput}
          onSearchInputChange={setEmpSearchInput}
          onSubmitSearch={submitEmpSearch}
        />
      </VStack>

      {/* Edit business modal */}
      {editModal.open && (
        <BusinessChainFormModal
          isOpen={editModal.open}
          onClose={editModal.onClose}
          business={business}
        />
      )}

      {/* Affiliate union modal */}
      {affiliateModal.open && (
        <AffiliateUnionModal
          isOpen={affiliateModal.open}
          onClose={affiliateModal.onClose}
          businessId={business.id}
        />
      )}

      {/* Link existing worksite modal */}
      {linkWorksiteModal.open && (
        <LinkExistingWorksiteModal
          isOpen={linkWorksiteModal.open}
          onClose={linkWorksiteModal.onClose}
          businessId={business.id}
        />
      )}

      {/* Add worksite modal */}
      {addWorksiteModal.open && (
        <BusinessFormModal
          isOpen={addWorksiteModal.open}
          onClose={addWorksiteModal.onClose}
          lockedBusinessId={business.id}
          onCreated={(wsId) => navigate(`/dashboard/worksites/${wsId}`)}
        />
      )}

      {/* Delete confirmation */}
      <Dialog.Root
        open={showDeleteDialog}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setShowDeleteDialog(false);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">Delete Business</Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>{business.label}</strong>? This will deactivate the business record.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    ml={3}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Box>
  );
}
